/**
 * Client for the app-logic debugger WebSocket (ADR-0056, S4).
 *
 * Drives `WS /api/admin/apps/{appId}/queries/{queryId}/debug` — the server streams
 * `stopped` / `terminated` events, we send `setBreakpoints` / step / `vars` / `evaluate`.
 * The JWT rides as `?token=` (a WebSocket can't set an Authorization header, same as the
 * SSE event stream). Framework-agnostic + unit-testable via an injected `WebSocket` ctor.
 */

import { api } from "@/lib/api";

export type StepKind = "continue" | "stepOver" | "stepIn" | "stepOut";

/** The editor lines a breakpoint can bind to (QuickJS pc2line, ADR-0056). `available`
 *  is false on an old debug engine that can't extract them → the UI allows any line. */
export interface BreakpointLines {
  available: boolean;
  lines: number[];
}

/** Fetch the executable (breakpoint-bindable) editor lines of a code Query's compiled body.
 *  Used to confine the gutter so a breakpoint can't sit on a non-executable line. */
export function fetchBreakpointLines(appId: string, queryId: string): Promise<BreakpointLines> {
  return api.get<BreakpointLines>(`/apps/${appId}/queries/${queryId}/debug/breakpoint-lines`);
}

export interface DebugVar {
  name: string;
  value: string;
}

/** A server → client frame. */
export type DebugEvent =
  | { type: "ready" }
  | { type: "stopped"; reason: string; line: number | null }
  | { type: "terminated"; ok: boolean; value: unknown; error: string | null }
  | { type: "vars"; vars: DebugVar[] }
  | { type: "eval"; result: string; expression: string }
  | { type: "error"; error: string };

export interface DebugHandlers {
  onEvent: (ev: DebugEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

type WsCtor = new (url: string) => WebSocket;

/** `WebSocket.OPEN` — the numeric literal (1), so a mocked ctor without the static works. */
const WS_OPEN = 1;

/** Build the authed debug WS URL from the current origin (ws/wss mirrors http/https). */
export function debugSocketUrl(
  appId: string,
  queryId: string,
  token: string,
  input?: Record<string, unknown> | null,
  origin: { protocol: string; host: string } = window.location,
): string {
  const proto = origin.protocol === "https:" ? "wss:" : "ws:";
  const path = `/api/admin/apps/${appId}/queries/${queryId}/debug`;
  let url = `${proto}//${origin.host}${path}?token=${encodeURIComponent(token)}`;
  // The Prova/contract input rides as `?input=<json>` so the stepped body sees `ctx.input`
  // (else it always debugs with `{}`).
  if (input && Object.keys(input).length > 0) {
    url += `&input=${encodeURIComponent(JSON.stringify(input))}`;
  }
  return url;
}

/**
 * A live debug session over one WebSocket. Construct, `connect()`, then drive it; the
 * `onEvent` handler receives every server frame. `close()` tears the socket down.
 */
export class QueryDebugSession {
  private ws: WebSocket | null = null;

  constructor(
    private readonly url: string,
    private readonly handlers: DebugHandlers,
    private readonly WsImpl: WsCtor = WebSocket,
  ) {}

  connect(): void {
    const ws = new this.WsImpl(this.url);
    this.ws = ws;
    ws.onopen = () => this.handlers.onOpen?.();
    ws.onclose = () => this.handlers.onClose?.();
    ws.onmessage = (ev: MessageEvent) => {
      let parsed: DebugEvent;
      try {
        parsed = JSON.parse(String(ev.data));
      } catch {
        return; // ignore a malformed frame rather than crash the session
      }
      this.handlers.onEvent(parsed);
    };
  }

  private send(msg: Record<string, unknown>): void {
    if (this.ws && this.ws.readyState === WS_OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  setBreakpoints(lines: number[]): void {
    this.send({ cmd: "setBreakpoints", lines });
  }

  resume(kind: StepKind = "continue"): void {
    this.send({ cmd: kind });
  }

  requestVars(frameId = 0): void {
    this.send({ cmd: "vars", frameId });
  }

  evaluate(expression: string, frameId = 0): void {
    this.send({ cmd: "evaluate", expression, frameId });
  }

  stop(): void {
    this.send({ cmd: "stop" });
  }

  close(): void {
    try {
      this.ws?.close();
    } catch {
      /* already closed */
    }
    this.ws = null;
  }
}
